import axios from 'axios';

/**
 * Public API client — gọi Backend (Express + MongoDB) để lấy nội dung động
 * (phí, ngành hàng, ebook, config) và gửi form tư vấn.
 * KHÔNG dùng auth interceptor như axiosClient (đây là dữ liệu công khai).
 */
const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Types ──────────────────────────────────────────
export type ShopType = 'mall' | 'thuong';

export interface Fee {
  _id: string;
  feeKey: string;
  label: string;
  unit: '%' | 'VND';
  shopTypes: ShopType[];
  optional: boolean;
  optionalLabel: string;
  capped: boolean;
  maxValue: number;
  valueMall: number;
  valueThuong: number;
  sortOrder: number;
  isActive: boolean;
}

export interface LoaiSanPham {
  tenLoai: string;
  phiHoaHong: string;
  loaiCon?: { tenLoai: string; phiHoaHong: string }[];
}
export interface NganhHang {
  _id: string;
  tenNganh: string;
  loaiSanPham: LoaiSanPham[];
  loaiShop: 'mall' | 'regular';
}

export interface EbookDto {
  _id: string;
  slug: string;
  title: string;
  titleEn?: string;
  description?: string;
  descriptionEn?: string;
  imageUrl?: string;
  driveLink?: string;
  driveLinkEn?: string;
  sortOrder: number;
  isActive: boolean;
}

export interface ServiceMenuItem {
  label: string;
  url: string;
}

export interface AppConfig {
  dataVersion: string;
  consultationServices: string[];
  zaloGroupLink: string;
  shopeeServices: ServiceMenuItem[];
  consultationImage: string;
}

export interface Footer {
  companyName: string;
  taxNumber: string;
  issueDate: string;
  issuer: string;
  representative: string;
  addresses: string[];
  phone: string;
  email: string;
  websiteLabel: string;
  websiteUrl: string;
  facebookUrl: string;
  zaloUrl: string;
  youtubeUrl: string;
  hotline: string;
  copyright: string;
  description: string;
}

// Fallback khi footer chưa tải xong / lỗi (BE trả giá trị thật khi có).
export const DEFAULT_FOOTER: Footer = {
  companyName: 'CÔNG TY CỔ PHẦN TẬP ĐOÀN TUKIGROUP',
  taxNumber: '0109789110',
  issueDate: '25/10/2021',
  issuer: 'SỞ KẾ HOẠCH VÀ ĐẦU TƯ THÀNH PHỐ HÀ NỘI',
  representative: 'NGUYỄN TRUNG KIÊN',
  addresses: [
    'Địa chỉ tại Hà Nội: Số 35 Lê Văn Lương, Thanh Xuân, Hà Nội.',
    'Địa chỉ tại TP Hồ Chí Minh: 65 Đ. Nguyễn Minh Hoàng, Phường 12, Tân Bình, Hồ Chí Minh.',
  ],
  phone: '(+84) 0976.657.215 - (+84) 0868.388.862',
  email: 'tukigroup123@gmail.com',
  websiteLabel: 'https://www.facebook.com/tukigroupvn',
  websiteUrl: 'https://www.facebook.com/tukigroupvn',
  facebookUrl: 'https://www.facebook.com/tukigroupvn',
  zaloUrl: 'https://zalo.me/675526800181191781',
  youtubeUrl: 'https://www.youtube.com/@nguyentrungkien3469',
  hotline: '0834713331',
  copyright: '©2025 Tuki Group. Tất cả quyền được bảo lưu.',
  description:
    'Công cụ tính giá sản phẩm Shopee chuyên nghiệp. Giúp bạn tối ưu lợi nhuận và định giá sản phẩm chính xác.',
};

export interface ConsultationPayload {
  fullName: string;
  phoneNumber: string;
  shopIssue: string;
  revenueScale: string;
  shopLink: string;
  prizeWon?: string;
  source?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

// ─── API ────────────────────────────────────────────
export const cmsApi = {
  async getChiPhi(): Promise<Fee[] | null> {
    const res = await client.get<ApiResponse<Fee[]>>('/api/chi-phi');
    return res.data?.data ?? null;
  },

  async getNganhHang(loaiShop: 'mall' | 'regular'): Promise<NganhHang[]> {
    const res = await client.get<ApiResponse<NganhHang[]>>(`/api/nganh-hang?loaiShop=${loaiShop}`);
    return res.data?.data ?? [];
  },

  async getEbooks(): Promise<EbookDto[]> {
    const res = await client.get<ApiResponse<EbookDto[]>>('/api/ebook');
    return res.data?.data ?? [];
  },

  async getConfig(): Promise<AppConfig | null> {
    const res = await client.get<ApiResponse<AppConfig>>('/api/config');
    return res.data?.data ?? null;
  },

  async getFooter(): Promise<Footer | null> {
    const res = await client.get<ApiResponse<Footer>>('/api/config/footer');
    return res.data?.data ?? null;
  },

  async submitConsultation(payload: ConsultationPayload): Promise<{ success: boolean; message: string }> {
    const res = await client.post<ApiResponse<unknown>>('/api/tu-van', payload);
    return { success: !!res.data?.success, message: res.data?.message ?? '' };
  },
};

export default cmsApi;
